import React from 'react';

/**
 * SIA (Setu Intelligent Assistant) Orb Component
 * Consistent design across the entire app
 * 
 * Design: Gradient cyan-purple border, dark inner with floating spheres
 * - Large cyan sphere at bottom
 * - Small yellow/white sphere at top-left
 * - Small pink dot at bottom-right
 * - Floating pink accent orb outside
 */

export const SIAOrb = ({ 
  isSpeaking = false, 
  size = 'medium', 
  onClick,
  className = '',
  showAccent = true 
}) => {
  const sizeConfig = {
    small: {
      container: 'w-10 h-10',
      innerSphere: 'w-4 h-4',
      yellowDot: 'w-1.5 h-1.5',
      pinkDot: 'w-1 h-1',
      accentOrb: 'w-3 h-3 -top-0.5 -right-0.5',
      glowInset: '-inset-2',
      pulseRing1: '-inset-3',
      pulseRing2: '-inset-2',
    },
    medium: {
      container: 'w-14 h-14',
      innerSphere: 'w-6 h-6',
      yellowDot: 'w-2 h-2',
      pinkDot: 'w-1.5 h-1.5',
      accentOrb: 'w-4 h-4 -top-1 -right-1',
      glowInset: '-inset-3',
      pulseRing1: '-inset-4',
      pulseRing2: '-inset-3',
    },
    large: {
      container: 'w-20 h-20',
      innerSphere: 'w-8 h-8',
      yellowDot: 'w-2.5 h-2.5',
      pinkDot: 'w-2 h-2',
      accentOrb: 'w-5 h-5 -top-1 -right-1',
      glowInset: '-inset-4',
      pulseRing1: '-inset-5',
      pulseRing2: '-inset-4',
    },
  };
  
  const config = sizeConfig[size] || sizeConfig.medium;
  
  const Component = onClick ? 'button' : 'div';
  
  return (
    <Component 
      className={`relative ${config.container} ${className}`}
      onClick={onClick}
      data-testid="sia-orb"
    >
      {/* Pulse rings when speaking */}
      {isSpeaking && (
        <>
          <div 
            className={`absolute ${config.pulseRing1} rounded-full border border-cyan-400/40 animate-ping`} 
            style={{ animationDuration: '2s' }} 
          />
          <div 
            className={`absolute ${config.pulseRing2} rounded-full border border-purple-400/30 animate-ping`} 
            style={{ animationDuration: '2.5s' }} 
          />
        </>
      )}
      
      {/* Outer glow */}
      <div className={`absolute inset-0 rounded-full blur-xl transition-all duration-500 ${
        isSpeaking ? 'bg-cyan-400/40 scale-150' : 'bg-purple-500/20'
      }`} />
      
      {/* Main orb container with shadow */}
      <div className={`relative w-full h-full rounded-full transition-all duration-300 ${
        isSpeaking 
          ? 'shadow-[0_0_40px_rgba(34,211,238,0.5),0_0_80px_rgba(139,92,246,0.3)]' 
          : 'shadow-[0_0_20px_rgba(139,92,246,0.3)]'
      }`}>
        {/* Gradient border ring */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 via-purple-500 to-cyan-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-[#0a0a1a]" />
        </div>
        
        {/* Inner content */}
        <div className="absolute inset-[3px] rounded-full bg-gradient-to-br from-[#0f1029] to-[#0a0a1a] overflow-hidden">
          {/* Large cyan sphere - bottom center */}
          <div className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 ${config.innerSphere} rounded-full bg-gradient-to-br from-cyan-300 via-cyan-500 to-blue-600 opacity-90`}>
            {/* Light reflection on sphere */}
            <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-white/70 blur-[1px]" />
          </div>
          
          {/* Small yellow/white sphere - top left */}
          <div className={`absolute top-2 left-2 ${config.yellowDot} rounded-full bg-gradient-to-br from-yellow-100 to-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.6)]`} />
          
          {/* Small pink dot - bottom right */}
          <div className={`absolute bottom-3 right-2 ${config.pinkDot} rounded-full bg-pink-400 shadow-[0_0_6px_rgba(236,72,153,0.6)]`} />
          
          {/* Purple overlay glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-1/2 h-1/2 rounded-full bg-gradient-to-br from-purple-400/20 to-transparent blur-sm" />
        </div>
      </div>
      
      {/* Floating pink accent orb - outside main orb */}
      {showAccent && (
        <div className={`absolute ${config.accentOrb} rounded-full bg-gradient-to-br from-pink-400 to-purple-500 shadow-[0_0_12px_rgba(236,72,153,0.6)] transition-transform duration-500 ${
          isSpeaking ? 'scale-125 animate-bounce' : ''
        }`} style={{ animationDuration: '2s' }} />
      )}
    </Component>
  );
};

export default SIAOrb;
